import { useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';

export interface Message {
  role: 'user' | 'assistant';
  content: string;
}

export function useAISpecificationChat(productCategoryId: string) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const abortControllerRef = useRef<AbortController | null>(null);
  
  // Generate and maintain conversation ID across the session
  const conversationIdRef = useRef<string>(crypto.randomUUID());

  // Prepare messages with summarization for long conversations
  const prepareMessagesForAPI = (messageList: Message[]): Message[] => {
    const CONVERSATION_THRESHOLD = 12; // More than 6 exchanges
    const KEEP_RECENT = 8; // Keep last 4 exchanges
    
    if (messageList.length <= CONVERSATION_THRESHOLD) {
      return messageList;
    }
    
    // Summarize older messages
    const oldMessages = messageList.slice(0, messageList.length - KEEP_RECENT);
    const recentMessages = messageList.slice(-KEEP_RECENT);
    
    // Create summary of older conversation
    const topics = new Set<string>();
    const requirements = new Set<string>();
    
    oldMessages.forEach(msg => {
      const content = msg.content.toLowerCase();
      // Extract potential topics (simple heuristic)
      if (content.includes('size') || content.includes('dimension')) topics.add('dimensions');
      if (content.includes('color') || content.includes('colour')) topics.add('colors');
      if (content.includes('material') || content.includes('paper')) topics.add('materials');
      if (content.includes('quantity') || content.includes('amount')) topics.add('quantities');
      if (content.includes('delivery') || content.includes('shipping')) topics.add('delivery');
      if (content.includes('finish') || content.includes('coating')) topics.add('finishing');
    });
    
    const summaryText = `[Earlier conversation (${oldMessages.length} messages): Discussed ${
      topics.size > 0 ? Array.from(topics).join(', ') : 'product specifications'
    }. Key requirements captured.]`;
    
    return [
      { role: 'user', content: summaryText },
      ...recentMessages
    ];
  };

  const sendMessage = async (userMessage: string) => {
    if (!userMessage.trim() || isLoading) return;

    const newUserMessage: Message = { role: 'user', content: userMessage };
    const updatedMessages = [...messages, newUserMessage];
    setMessages(updatedMessages);
    setIsLoading(true);
    setStreamingContent('');

    try {
      abortControllerRef.current = new AbortController();

      const { data: { session } } = await supabase.auth.getSession();
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/spec-assistant`;
      
      // CRITICAL: Use full message history for accurate AI summaries during chat.
      // The AI needs complete context to generate summaries that match extracted data.
      // Prompt caching mitigates the token cost of longer conversations.
      const apiMessages = updatedMessages;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          productCategoryId,
          messages: apiMessages,
          conversationId: conversationIdRef.current,
          action: 'chat'
        }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) throw new Error('Failed to get response');

      // Parse streaming response
      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let assistantContent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.type === 'content_block_delta') {
                const text = data.delta?.text || '';
                assistantContent += text;
                setStreamingContent(assistantContent);
              }
            } catch (e) {
              // Ignore parse errors for incomplete chunks
            }
          }
        }
      }

      // Finalize assistant message
      setMessages([...updatedMessages, { role: 'assistant', content: assistantContent }]);
      setStreamingContent('');

    } catch (error) {
      console.error('Error sending message:', error);
      setMessages([...updatedMessages, { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error. Please try again.' 
      }]);
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  };

  const extractSpecification = async () => {
    setIsLoading(true);
    try {
      // CRITICAL: Always send full messages for extraction
      // Summarization loses important details like names, counts, weights
      const { data, error } = await invokeEdgeFunction('spec-assistant', {
        body: {
          productCategoryId,
          messages: messages,
          action: 'extract',
          conversationId: conversationIdRef.current,
        },
        callerOperation: 'useAISpecificationChat.extract',
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error extracting specification:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const reset = useCallback(() => {
    setMessages([]);
    setStreamingContent('');
    setIsLoading(false);
    // Generate new conversation ID when resetting
    conversationIdRef.current = crypto.randomUUID();
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  return {
    messages,
    isLoading,
    streamingContent,
    sendMessage,
    extractSpecification,
    reset
  };
}
